import React, { useState, useEffect } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { orderAPI } from '../../utils/api';

const RetailerOrders = () => {
    const { showSuccess, showError } = useNotification();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOrders = async () => {
        try {
            const res = await orderAPI.getRetailerOrders();
            if (res.data.success) {
                setOrders(res.data.data || []);
            }
        } catch (error) {
            showError('Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (orderId, newStatus) => {
        if (!window.confirm(`Are you sure you want to mark this order as ${newStatus}?`)) return;

        try {
            const res = await orderAPI.updateStatus(orderId, newStatus);
            if (res.data.success) {
                showSuccess(`Order updated to ${newStatus}`);
                fetchOrders();
            }
        } catch (error) {
            console.error(error);
            showError(error.response?.data?.message || 'Failed to update status');
        }
    };

    if (loading) return <div className="text-center py-8">Loading orders...</div>;

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gold">Manage Orders</h1>

            {orders.length === 0 ? (
                <div className="text-center py-12 bg-gray-800 rounded-xl">
                    <p className="text-gray-400">No orders found</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {orders.map((order) => (
                        <div key={order._id} className="bg-gray-800 p-6 rounded-xl">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-semibold text-lg">Order #{order._id.slice(-6).toUpperCase()}</h3>
                                    <p className="text-sm text-gray-400">
                                        Placed on: {new Date(order.createdAt).toLocaleString()}
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        Status: <span className={`font-bold ${order.status === 'delivered' ? 'text-green-500' :
                                            order.status === 'cancelled' ? 'text-yellow-400' :
                                                order.status === 'paid' ? 'text-yellow-500' :
                                                    'text-blue-500'
                                            }`}>{order.status.toUpperCase()}</span>
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xl font-bold text-gold">₹{order.total_amount}</p>
                                    <p className="text-xs text-gray-500">Commission: {order.commission_percent}%</p>
                                    <p className="text-sm text-green-400">Net Earnings: ₹{order.retailer_earnings}</p>
                                </div>
                            </div>

                            {/* Items */}
                            <div className="bg-gray-700 p-4 rounded-lg mb-4">
                                <h4 className="text-sm font-semibold mb-2 text-gray-300">Items</h4>
                                <ul className="space-y-1">
                                    {order.items.map((item, idx) => (
                                        <li key={idx} className="flex justify-between text-sm">
                                            <span>{item.name} x {item.quantity}</span>
                                            <span>₹{item.price_per_unit * item.quantity}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Address */}
                            <div className="bg-gray-700 p-4 rounded-lg mb-4">
                                <h4 className="text-sm font-semibold mb-2 text-gray-300">Delivery Address</h4>
                                <p className="text-sm">{order.shipping_address.street}</p>
                                <p className="text-sm">{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.zip}</p>
                                <p className="text-sm text-gray-400">Phone: {order.patient_id?.phone || 'N/A'}</p>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 justify-end">
                                {order.status === 'paid' && (
                                    <button
                                        onClick={() => updateStatus(order._id, 'accepted')}
                                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700"
                                    >
                                        Accept Order
                                    </button>
                                )}
                                {order.status === 'accepted' && (
                                    <button
                                        onClick={() => updateStatus(order._id, 'dispatched')}
                                        className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700"
                                    >
                                        Dispatch
                                    </button>
                                )}
                                {order.status === 'dispatched' && (
                                    <button
                                        onClick={() => updateStatus(order._id, 'delivered')}
                                        className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700"
                                    >
                                        Mark Delivered
                                    </button>
                                )}
                                {['paid', 'accepted'].includes(order.status) && (
                                    <button
                                        onClick={() => updateStatus(order._id, 'cancelled')}
                                        className="bg-yellow-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-amber-600"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RetailerOrders;
